import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Input, { InputError, InputProps } from '@/components/ui/Form/Input';
import DatePicker from '@/components/ui/DatePicker';
import Panel from '@/components/ui/Panel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { formatDate, parseDate, DateFormat } from '@/utils/helpers';
import { getDateInputErrors } from './errors';
import { ValidationOptions, hasOnlyNumbers, isValidValue } from '@/utils/validations';
import YearChangeButton from '../../DatePicker/YearChangeButton';
import { endOfYear, startOfYear, parse, format, isValid } from 'date-fns';
import { RemoveScroll } from 'react-remove-scroll';
import { getScrollableParent } from '../../CreditTracker/util/TrackerHelpers';

const validationOptions: ValidationOptions = {
  acceptLetters: false,
  acceptNumbers: true,
  acceptSpaces: false,
  acceptSpecialChars: false,
  allowedChars: '/'
};

interface MinMaxDate {
  minDate?: Date;
  maxDate?: Date;
}

const roundPosition = (value?: number) =>
  typeof value === 'number' ? Math.round(value) : undefined;

const positionStylesAreEqual = (a: CSSProperties, b: CSSProperties) =>
  a.left === b.left && a.top === b.top && a.bottom === b.bottom;

const DatePickerPopover = {
  width: 368,
  height: 422,
  spacing: 10
} as const;

const getDatePickerStyle = (inputPosition: DOMRect): CSSProperties => {
  const { width, height, spacing } = DatePickerPopover;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const { top, bottom, left } = inputPosition;
  const spaceBelow = viewportHeight - bottom;
  const spaceAbove = top;
  const fitsBelow = spaceBelow >= height + spacing;
  const fitsAbove = spaceAbove >= height + spacing;
  const shouldOpenBelow = fitsBelow || spaceBelow >= spaceAbove;
  const maxLeft = Math.max(spacing, viewportWidth - width - spacing);

  return {
    left: roundPosition(Math.max(spacing, Math.min(left, maxLeft))),
    top: roundPosition(shouldOpenBelow ? bottom + spacing : fitsAbove ? undefined : spacing),
    bottom: roundPosition(
      shouldOpenBelow ? undefined : fitsAbove ? viewportHeight - top + spacing : undefined
    )
  };
};

export interface DateInputProps extends InputProps {
  minDate?: Date;
  maxDate?: Date;
  isStartDate?: boolean;
  isEndDate?: boolean;
  required?: boolean;
  inheritedErrors?: InputError[];
  inputContainerClassName?: string;
  initialPickerDate?: Date | null;
  datePickerClassName?: string;
  // hasYearPicker?: boolean;
}

const DateInput = ({
  minDate,
  maxDate,
  isStartDate,
  isEndDate,
  onChange,
  required = false,
  inheritedErrors,
  inputContainerClassName,
  initialPickerDate,
  datePickerClassName,
  label,
  ...inputProps
}: DateInputProps): JSX.Element => {
  const { value } = inputProps;
  const [expanded, setExpanded] = useState<boolean>(false);
  const [minMaxDate, setMinMaxDate] = useState<MinMaxDate>({ minDate: minDate, maxDate: maxDate });
  const [inputPosition, setInputPosition] = useState<DOMRect | undefined>();
  const [datePickerStyle, setDatePickerStyle] = useState<CSSProperties>({});
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const isExtraSmallScreen = useMediaQuery(ScreenSizeQueries.xsOnly);

  const selectedDate = useMemo(() => parseDate(value), [value]);

  const errors = useMemo(
    () => getDateInputErrors(minDate, maxDate, value),
    [minDate, maxDate, value]
  );

  useEffect(() => {
    if (isExtraSmallScreen) {
      setMinMaxDate({ minDate: undefined, maxDate: undefined });
    }

    if (isExtraSmallScreen || !expanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expanded, isExtraSmallScreen]);

  const handleOnFocus = useCallback(() => {
    setExpanded(false);
  }, []);

  const isTwoDigitYear = (input: string) => {
    const parts = input.split('/');
    return (
      parts.length === 3 &&
      parts[2].length === 2 &&
      !(parts[2].length === 2 && parts[2].slice(-1) === '0')
    );
  };

  const isFourDigitYear = (input: string) => {
    const parts = input.split('/');
    return parts.length === 3 && parts[2].length === 4;
  };

  const previousValueRef = useRef<string>('');

  const handleOnChange = useCallback(
    (newValue: string) => {
      const previousValue = previousValueRef.current;
      const parsedDate = isFourDigitYear(newValue)
        ? parse(newValue, 'M/d/yyyy', new Date())
        : parse(newValue, 'M/d/yy', new Date());

      if (!isValidValue(newValue, validationOptions)) {
        return;
      }

      if (newValue.length === 8 && hasOnlyNumbers(newValue)) {
        onChange?.(
          `${newValue.substring(0, 2)}/${newValue.substring(2, 4)}/${newValue.substring(4, 8)}`
        );
        return;
      }

      if (
        isValid(parsedDate) &&
        (isTwoDigitYear(newValue) || isFourDigitYear(newValue)) &&
        newValue.length > previousValue.length
      ) {
        const formattedDate = format(parsedDate, 'MM/dd/yyyy');
        onChange?.(formattedDate);
      } else {
        onChange?.(newValue);
      }

      previousValueRef.current = newValue;
    },
    [onChange]
  );

  const handleOnSelect = useCallback(
    (date: Date) => {
      onChange?.(formatDate(date));
      setExpanded(false);
    },
    [onChange]
  );

  const handleDatePickerVisibility = useCallback(() => {
    setExpanded(state => !state);
  }, []);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      setInputPosition(rect);
    };

    updatePosition();
    const scrollableParent = getScrollableParent(inputRef.current) ?? document.body;
    scrollableParent.addEventListener('scroll', updatePosition);
    scrollableParent.addEventListener('animationend', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollableParent.removeEventListener('scroll', updatePosition);
      scrollableParent.removeEventListener('animationend', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  useEffect(() => {
    if (!inputPosition) return;

    setDatePickerStyle(prev => {
      const next = getDatePickerStyle(inputPosition);

      return positionStylesAreEqual(prev, next) ? prev : next;
    });
  }, [inputPosition]);

  const datePicker = !isExtraSmallScreen && expanded && (
    <DatePicker
      ref={datePickerRef}
      isMobile={false}
      initialMonth={selectedDate ?? initialPickerDate ?? undefined}
      selectedDate={selectedDate}
      onSelect={handleOnSelect}
      minDate={minDate}
      maxDate={maxDate}
      isStartDate={isStartDate}
      isEndDate={isEndDate}
      style={datePickerStyle}
      popoverClassName={datePickerClassName}
    />
  );

  return (
    <div>
      <div ref={inputRef}>
        <Input
          className="mb-3"
          {...inputProps}
          messageStatusContainerClassName="relative"
          placeholder={DateFormat.toUpperCase()}
          iconName="upcoming"
          iconAccessibilityLabel="Open calendar"
          onIconClick={handleDatePickerVisibility}
          maxLength={DateFormat.length}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          errors={
            inheritedErrors?.some(x => x.message !== undefined)
              ? errors.concat(inheritedErrors)
              : errors
          }
          required={required}
          inputContainerClassName={inputContainerClassName}
          label={required ? `${label} *` : label}
        />
      </div>
      {datePicker && <RemoveScroll>{datePicker}</RemoveScroll>}
      <Panel isOpen={isExtraSmallScreen && expanded} onOpenChange={setExpanded}>
        <Panel.Content
          side="right"
          hasHorizontalPadding={false}
          isNestedModal={true}
          centerHeaderContent={
            <YearChangeButton
              onYearChange={year =>
                setMinMaxDate({
                  minDate: startOfYear(
                    selectedDate?.setFullYear(year) ?? new Date().setFullYear(year)
                  ),
                  maxDate: endOfYear(
                    selectedDate?.setFullYear(year) ?? new Date().setFullYear(year)
                  )
                })
              }
              currentDate={selectedDate ?? new Date()}
            />
          }
          onBackButtonPress={() => setExpanded(false)}
        >
          <Panel.Body>
            <DatePicker
              isMobile={true}
              initialMonth={selectedDate ?? initialPickerDate ?? undefined}
              selectedDate={selectedDate}
              onSelect={handleOnSelect}
              minDate={minMaxDate.minDate}
              maxDate={minMaxDate.maxDate}
              isStartDate={isStartDate}
              isEndDate={isEndDate}
            />
          </Panel.Body>
        </Panel.Content>
      </Panel>
    </div>
  );
};

export default DateInput;
